'use client';
import { useEffect } from 'react';
import { Course } from '@/types/data';
import { useDiscount } from '@/hooks/useDiscount';

interface CourseMetadataProps {
  course: Course | null;
}

const CourseMetadata: React.FC<CourseMetadataProps> = ({ course }) => {
  const { calculateDiscountedPrice } = useDiscount();

  useEffect(() => {
    if (!course) return;

    // Update document title
    const stripHtml = (html: string) => {
      return html
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const title = course.name;
    document.title = `${title} - OME Việt Nam`;

    // Update meta description
    const description = stripHtml(course.description || '').substring(0, 160) + '...';
    const teacherName = course.teacher?.name || 'Giảng viên OME';
    const originalPrice = course.price_promo ? course.price_promo : course.price;
    const price = calculateDiscountedPrice(originalPrice);

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      'content',
      `${description} Giảng viên: ${teacherName}. Thời gian: ${course.time}. Số bài học: ${course.number_of_lessons}.`
    );

    // Update Open Graph tags
    const updateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Update Twitter tags
    const updateTwitterTag = (name: string, content: string) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Update Open Graph tags
    updateMetaTag('og:title', `${title} - OME Việt Nam`);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', window.location.href);
    updateMetaTag('og:type', 'course');
    updateMetaTag('og:image', course.image);
    updateMetaTag('og:site_name', 'OME Việt Nam');
    updateMetaTag('og:locale', 'vi_VN');

    // Update Twitter tags
    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', `${title} - OME Việt Nam`);
    updateTwitterTag('twitter:description', description);
    updateTwitterTag('twitter:image', course.image);
    updateTwitterTag('twitter:creator', '@OME');
    updateTwitterTag('twitter:site', '@OME');

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', window.location.href);

    // Update structured data for course
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: course.name,
      description: course.description,
      provider: {
        '@type': 'Organization',
        name: 'OME Việt Nam',
        url: 'https://www.khoahoconline-ome.edu.vn/',
      },
      instructor: {
        '@type': 'Person',
        name: teacherName,
      },
      coursePrerequisites: course.description,
      educationalLevel: 'Intermediate',
      inLanguage: 'vi',
      isAccessibleForFree: false,
      offers: {
        '@type': 'Offer',
        price: price,
        priceCurrency: 'VND',
        availability: 'https://schema.org/InStock',
      },
      timeRequired: course.time,
      numberOfCredits: course.number_of_lessons,
      courseMode: 'online',
      image: course.image,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': window.location.href,
      },
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, [course]);

  return null; // This component doesn't render anything
};

export default CourseMetadata;
